import {inject, injectable} from "inversify";
import {Player, PlayerContractNegotiation} from "@/modules/team/entity";
import {EventEmitter} from "@/modules/base/events";
import {BeforePlayerContractTerminatedEvent} from "@/modules/team/events/BeforePlayerContractTerminatedEvent";
import {AfterPlayerContractTerminatedEvent} from "@/modules/team/events/AfterPlayerContractTerminatedEvent";
import {TransferListing, TransferLog, TransferOffer} from "@/modules/transfer-market/entity";
import {Season} from "@/modules/season/entity";
import {Notifier} from "@/modules/notifications/services/Notifier";
import {PlayerContractTerminatedNotification} from "@/modules/team/entity/PlayerContractTerminatedNotification";
import {TransferOfferHandler} from "@/modules/transfer-market/services/TransferOfferHandler";
import {Suspension} from "@/modules/competitions/entity";
import {NegotiationCloser} from "@/modules/diplomacy/services/NegotiationCloser";

@injectable()
export default class PlayerContractTerminator {
  constructor(
    @inject(NegotiationCloser) private negotiationCloser: NegotiationCloser,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(Notifier) private notifier: Notifier,
    @inject(TransferOfferHandler) private transferOfferHandler: TransferOfferHandler,
  ) {
  }

  async terminate(player: Player, price: number) {
    await this.em.emitAsync(new BeforePlayerContractTerminatedEvent(player));

    await this.notifier.notify(new PlayerContractTerminatedNotification().setPayload(player, price));

    const listing = await TransferListing
      .createQueryBuilder('listing')
      .innerJoinAndSelect('listing.player', 'player')
      .leftJoinAndSelect('listing.offers', 'offer')
      .getOne();

    if (listing) {
      await listing.offers.forEachAsync((o: TransferOffer) => this.transferOfferHandler.close(o));
      await listing.remove();
    }

    const suspensions = await Suspension
      .createQueryBuilder('suspension')
      .innerJoinAndSelect('suspension.player', 'player')
      .where('player.id = :playerId', {playerId: player.id})
      .getMany();

    await suspensions.forEachAsync((s: Suspension) => s.remove());

    await (
      await PlayerContractNegotiation
        .createQueryBuilder('n')
        .innerJoinAndSelect('n.target', 'player')
        .where('player.id = :playerId', {playerId: player.id})
        .getMany()
    )
      .forEachAsync(async (n: PlayerContractNegotiation) => {
        await this.negotiationCloser.abort(n);
      });

    const season = await Season.current();

    const log = new TransferLog();
    log.season = season;
    log.player = player;
    log.from = player.team;
    log.price = 0;
    log.transferredAt = season.currentDateRaw;
    await log.save();

    player.contractYears = null;
    player.team = null;
    player.transferred = true;
    await player.save();

    await this.em.emitAsync(new AfterPlayerContractTerminatedEvent(player));
  }
}
